import React, { useContext, useEffect } from "react";
import { useFormikContext } from "formik";
import { useAlert } from "react-alert";
import { components as rsComponents } from "react-select";
import { Location, FieldGroup } from "../../components";
import { Button, Toggle } from "../../components/ui";
import {
  boxOptions,
  conditionOptions,
  locationOptions,
  sellerTypeOptions,
  sourceOptions,
  yearOptions,
} from "../../product/constants";
import { getBaseCurrency } from "../../core/currency";

// Every filter below is a multi-select, and each control is pinned to the height
// of the REGION dropdown (an earlier review item: "must match the height of the
// REGION dropdown, even after value selection"). Left to itself react-select
// wraps one chip per selection, so with SOURCE's 15 options the chips grew to
// 252px inside a 42px box and the overflow was simply sliced — the next review
// then reported "Cropped items".
//
// Fixed height and no cropping are only compatible if the chips condense, so
// past the second one they collapse into a "+N" pill. Nothing is hidden: the
// count is always visible, and every value stays removable from the menu.
// One chip, not two. react-select's typing area is itself a greedy flex item, so
// the row is tighter than it looks: with two chips both hit their min-width floor
// and ellipsized even on desktop ("Trad…" / "Insta…"). One chip at full width
// plus the counter reads better and leaves headroom at 375px.
const MAX_VISIBLE_CHIPS = 1;

const CondensedMultiValue = ({ index, getValue, ...props }) => {
  const total = getValue().length;

  if (index < MAX_VISIBLE_CHIPS) {
    return <rsComponents.MultiValue index={index} getValue={getValue} {...props} />;
  }
  // One overflow pill for the whole remainder, rendered on the first hidden
  // index and suppressed for the rest.
  if (index === MAX_VISIBLE_CHIPS) {
    return (
      <span
        className="rc-alert-form__chip-overflow"
        title={getValue()
          .slice(MAX_VISIBLE_CHIPS)
          .map((o) => o.label)
          .join(", ")}
      >
        +{total - MAX_VISIBLE_CHIPS}
      </span>
    );
  }
  return null;
};

// react-select `components` hook — ui/Select has no condensed-chip slot, so
// these six stay on the legacy path (`legacySelect`).
const MULTI_SELECT_COMPONENTS = { MultiValue: CondensedMultiValue };

// Client review: the 4 toggles here should "reuse the style from that of
// the toggles on Listings page" — those use the shared bare Toggle
// (components/ui/Toggle), not FieldGroup's type="switch" (a react-switch
// instance only reachable today via CSS overrides in
// _rc-legacy-darkmode.scss). Keeps the same .field-group-switch wrapper/
// label markup FieldGroup rendered, so existing layout CSS still applies —
// only the switch element itself changes.
const AlertToggleField = ({ name, label, values, setFieldValue }) => (
  <div className="field-group field-group-switch">
    <Toggle
      bare
      size="md"
      checked={!!values[name]}
      onChange={(next) => setFieldValue(name, next)}
    />
    <label htmlFor={name}>{label}</label>
  </div>
);

const FilterForms = ({ setStatus }) => {
  const alert = useAlert();
  const {
    values,
    submitForm,
    setSubmitting,
    setFieldValue,
    touched,
    errors,
    setFieldTouched,
  } = useFormikContext();

  const handleActivation = () => {
    submitForm();
    setFieldTouched("sources", true);
    setStatus(true);
  };

  const handleSave = () => {
    submitForm();
    setFieldTouched("sources", true);
  };

  useEffect(() => {
    if (values.is_not_product && values.is_only_brand) {
      alert.show(
        "You cannot enable both 'Only Model' and 'Only Brand + Keywords'."
      );
      setFieldValue("is_not_product", false);
    }
  }, [values.is_not_product]);

  useEffect(() => {
    if (values.is_not_product && values.is_only_brand) {
      alert.show(
        "You cannot enable both 'Only Model' and 'Only Brand + Keywords'."
      );
      setFieldValue("is_only_brand", false);
    }
  }, [values.is_only_brand]);

  return (
    <div className="listing-fields">
      <div className="row">
        <div className="col-6 col-xl-4 left-col min-budget">
          <FieldGroup
            name="min_price"
            type="money"
            currency={getBaseCurrency().toUpperCase()}
            placeholder="Min budget"
          />
        </div>
        <div className="col-6 col-xl-4 max-budget">
          <FieldGroup
            name="max_price"
            type="money"
            currency={getBaseCurrency().toUpperCase()}
            placeholder="Max budget"
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-xl-4 right-col">
          <FieldGroup
            name="years"
            type="select"
            options={yearOptions}
            placeholder="Year"
            isMulti
            components={MULTI_SELECT_COMPONENTS}
            legacySelect
          />
        </div>
        <div className="col-12 col-xl-4 right-col">
          <FieldGroup
            name="conditions"
            type="select"
            options={conditionOptions}
            placeholder="Condition"
            isMulti
            components={MULTI_SELECT_COMPONENTS}
            legacySelect
          />
        </div>
        <div className="col-12 col-xl-4 right-col">
          <FieldGroup
            name="delivery_sets"
            type="select"
            options={boxOptions}
            placeholder="Delivery Set"
            isMulti
            components={MULTI_SELECT_COMPONENTS}
            legacySelect
          />
        </div>
        <div className="col-12 col-xl-4 right-col">
          <FieldGroup
            name="regions"
            type="select"
            options={locationOptions}
            placeholder="Region"
            isMulti
            components={MULTI_SELECT_COMPONENTS}
            legacySelect
          />
        </div>
        <div className="col-12 col-xl-4 right-col">
          <FieldGroup
            name="sources"
            type="select"
            options={sourceOptions}
            placeholder="Source"
            isMulti
            components={MULTI_SELECT_COMPONENTS}
            legacySelect
          />
        </div>
        <div className="col-12 col-xl-4 right-col">
          <FieldGroup
            name="seller_types"
            type="select"
            options={sellerTypeOptions}
            placeholder="Seller Types"
            isMulti
            components={MULTI_SELECT_COMPONENTS}
            legacySelect
          />
        </div>
      </div>
      <div className="row">
        <FieldGroup label="Keywords" name="keywords" />
      </div>
      <div className="row">
        <div className="col-12 col-xl-2 right-col">
          <AlertToggleField
            label="Alert"
            name="alert"
            values={values}
            setFieldValue={setFieldValue}
          />
        </div>
        <div className="col-12 col-xl-2 right-col">
          <AlertToggleField
            label="Deal Finder"
            name="underpriced"
            values={values}
            setFieldValue={setFieldValue}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-xl-2 right-col">
          <AlertToggleField
            label="Only Model"
            name="is_not_product"
            values={values}
            setFieldValue={setFieldValue}
          />
        </div>
        <div className="col-12 col-xl-3 right-col">
          <AlertToggleField
            label="Only Brand + Keywords"
            name="is_only_brand"
            values={values}
            setFieldValue={setFieldValue}
          />
        </div>
      </div>
      <div className="row mt-auto">
        <div className="col-md-12">
          {/* Reviewer (design): "save / activate, we dont have any buttons with
              this colour + shape. We need to be consistent in our design."
              Both were true and measurable:
              - `variant="success"` was the ONLY use of the green gradient in the
                whole app (1/29 variant usages), so it matched nothing else.
                `primary` is the sanctioned action colour (the purple gradient
                already used for the Lookbook main action).
              - Both defaulted to `size="md"` (32px) while every field in this
                same form is 44px, so the action row read 12px short of the
                controls above it. `xl` is the 44px token. */}
          <div className="action-row mb-0">
            <Button type="button" variant="secondary" size="xl" onClick={() => handleSave()}>
              Save
            </Button>
            <Button type="button" variant="primary" size="xl" onClick={() => handleActivation()}>
              Activate
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterForms;
